import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';
import { AuthContext } from '../context/AuthContext';
import API_BASE_URL, { API_BASE_URL_WITHOUT_API } from '../config/api';
import LISTING_VIEWS from '../config/listingViews';
import './ListingView.css';

/* ------------------------------- helpers -------------------------------- */

const get = (object, path) =>
  path.split('.').reduce((value, key) => (value == null ? undefined : value[key]), object);

const isBlank = (value) =>
  value === null ||
  value === undefined ||
  value === '' ||
  (Array.isArray(value) && value.length === 0);

const imageUrl = (path) => {
  if (!path) return null;
  return path.startsWith('http') ? path : `${API_BASE_URL_WITHOUT_API}${path}`;
};

const formatDate = (value) => {
  if (!value) return null;
  try {
    return new Date(value).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return String(value);
  }
};

const money = (value, currency = 'INR') => {
  if (value === null || value === undefined || value === '') return null;
  const amount = Number(value);
  if (Number.isNaN(amount)) return String(value);
  try {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: currency || 'INR',
      maximumFractionDigits: 0,
    }).format(amount);
  } catch {
    return `${currency} ${amount}`;
  }
};

/** Turns a populated ref, plain string or object into something printable. */
const labelOf = (item) => {
  if (item == null) return '';
  if (typeof item === 'string') return item;
  return item.name || item.title || item.serviceName || item.optionName || item.variantName || '';
};

/* ------------------------------ primitives ------------------------------- */

const Field = ({ label, value }) =>
  isBlank(value) ? null : (
    <div className="lv-field">
      <span className="lv-field-label">{label}</span>
      <span className="lv-field-value">{value}</span>
    </div>
  );

const Chips = ({ items }) => (
  <div className="lv-chips">
    {items.map((item, index) => (
      <span className="lv-chip" key={`${labelOf(item)}-${index}`}>{labelOf(item)}</span>
    ))}
  </div>
);

const Gallery = ({ images, featured }) => {
  const all = [...new Set([featured, ...(images || [])].filter(Boolean))];
  const [active, setActive] = useState(all[0] || null);
  // Listings can reference files that are no longer on disk - show a note
  // rather than the browser's broken-image glyph.
  const [broken, setBroken] = useState({});

  useEffect(() => {
    setActive((current) => current || all[0] || null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [images, featured]);

  if (all.length === 0) {
    return <div className="lv-gallery-empty">No images uploaded</div>;
  }

  const markBroken = (path) => setBroken((prev) => ({ ...prev, [path]: true }));

  return (
    <div className="lv-gallery">
      <div className="lv-gallery-main">
        {broken[active] ? (
          <span className="lv-img-missing">Image unavailable</span>
        ) : (
          <img src={imageUrl(active)} alt="" onError={() => markBroken(active)} />
        )}
      </div>
      {all.length > 1 && (
        <div className="lv-gallery-strip">
          {all.map((path) => (
            <button
              type="button"
              key={path}
              className={`lv-thumb ${path === active ? 'is-active' : ''}`}
              onClick={() => setActive(path)}
              aria-label="Show image"
            >
              {broken[path] ? (
                <span className="lv-thumb-missing" aria-hidden="true">!</span>
              ) : (
                <img src={imageUrl(path)} alt="" onError={() => markBroken(path)} />
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

/* ------------------------------- sections -------------------------------- */

const Section = ({ title, subtitle, children }) => (
  <section className="lv-section">
    <header className="lv-section-head">
      <h2>{title}</h2>
      {subtitle && <p>{subtitle}</p>}
    </header>
    {children}
  </section>
);

/** One entry from a sub-collection (room type, service, option, variant, day). */
const ItemCard = ({ item, spec, currency }) => {
  const title = spec.title ? spec.title(item) : labelOf(item);
  const price = spec.price ? spec.price(item) : null;
  const cut = spec.cutPrice ? spec.cutPrice(item) : null;

  return (
    <article className="lv-item">
      <div className="lv-item-head">
        <strong>{title || 'Untitled'}</strong>
        {price != null && (
          <span className="lv-item-price">
            {cut != null && cut > price && <s>{money(cut, currency)}</s>}
            <b>{money(price, currency)}</b>
            {spec.priceSuffix && <small>{spec.priceSuffix}</small>}
          </span>
        )}
      </div>

      {spec.meta && (
        <div className="lv-item-meta">
          {spec.meta(item)
            .filter((entry) => !isBlank(entry.value))
            .map((entry) => (
              <span key={entry.label}>
                <i>{entry.label}</i> {entry.value}
              </span>
            ))}
        </div>
      )}

      {!isBlank(item.description) && <p className="lv-item-desc">{item.description}</p>}

      {spec.chips &&
        spec.chips(item).filter((group) => !isBlank(group.items)).map((group) => (
          <div className="lv-item-chips" key={group.label}>
            <span className="lv-item-chips-label">{group.label}</span>
            <Chips items={group.items} />
          </div>
        ))}
    </article>
  );
};

/* --------------------------------- page ---------------------------------- */

const ListingView = ({ type }) => {
  const config = LISTING_VIEWS[type];
  const { id } = useParams();
  const navigate = useNavigate();
  const { hasPermission } = useContext(AuthContext);

  const [record, setRecord] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  // True when the API says the record does not exist (deleted, or a stale
  // link such as an old subscription-history entry) rather than a load failure.
  const [missing, setMissing] = useState(false);

  const fetchRecord = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      setMissing(false);
      const res = await axios.get(`${API_BASE_URL}/admin/${config.endpoint}/${id}`);
      if (res.data?.success) {
        setRecord(res.data.data);
      } else {
        setError(res.data?.message || `Failed to load ${config.singular}`);
      }
    } catch (e) {
      const status = e?.response?.status;
      const message = e?.response?.data?.message || e?.response?.data?.error || '';
      // 404 = gone; a malformed id surfaces as a Mongoose cast error
      if (status === 404 || /Cast to ObjectId failed/i.test(message)) {
        setMissing(true);
      } else {
        setError(message || e.message || `Failed to load ${config.singular}`);
      }
    } finally {
      setLoading(false);
    }
  }, [config, id]);

  useEffect(() => {
    fetchRecord();
  }, [fetchRecord]);

  const listPath = `/sy-admin/${config.endpoint}`;

  if (loading) {
    return (
      <Layout>
        <div className="container listing-view">
          <div className="skeleton" style={{ width: 280, height: 30, marginBottom: 12 }} />
          <div className="skeleton" style={{ width: 200, height: 16, marginBottom: 24 }} />
          <div className="skeleton" style={{ height: 300, borderRadius: 16, marginBottom: 18 }} />
          <div className="skeleton" style={{ height: 200, borderRadius: 16 }} />
        </div>
      </Layout>
    );
  }

  if (missing) {
    return (
      <Layout>
        <div className="container listing-view">
          <div className="lv-head">
            <div>
              <h1>{config.label}</h1>
            </div>
            <Link to={listPath} className="btn btn-secondary">Back to list</Link>
          </div>
          <div className="lv-missing">
            <span className="lv-missing-icon" aria-hidden="true">🗑️</span>
            <h2>This {config.singular} no longer exists</h2>
            <p>
              It has been deleted, or the link you followed is out of date. Records that referred to it,
              such as subscription history, are kept for your reference.
            </p>
            <div className="lv-missing-actions">
              <Link to={listPath} className="btn btn-primary">Go to {config.label.toLowerCase()}</Link>
              <button type="button" className="btn btn-secondary" onClick={() => navigate(-1)}>Go back</button>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  if (error || !record) {
    return (
      <Layout>
        <div className="container listing-view">
          <div className="lv-head">
            <div>
              <h1>{config.label}</h1>
            </div>
            <Link to={listPath} className="btn btn-secondary">Back to list</Link>
          </div>
          <div className="error-message">{error || `Could not load this ${config.singular}`}</div>
          <button type="button" className="btn btn-secondary" onClick={fetchRecord} style={{ marginTop: 14 }}>
            Try again
          </button>
        </div>
      </Layout>
    );
  }

  const currency = record.currency || 'INR';
  const title = labelOf(record) || config.label;
  const canEdit = hasPermission(`${config.permission}.update`);

  return (
    <Layout>
      <div className="container listing-view">
        {/* ------------------------------ header ----------------------------- */}
        <div className="lv-head">
          <div className="lv-head-main">
            <Link to={listPath} className="lv-back">&larr; {config.label}</Link>
            <h1>{title}</h1>
            <div className="lv-badges">
              <span className={`lv-status lv-status-${record.status || 'pending'}`}>
                {record.status || 'pending'}
              </span>
              {record.featured && <span className="lv-flag lv-flag-featured">★ Featured</span>}
              {record.verified && <span className="lv-flag lv-flag-verified">✓ Verified</span>}
              {config.subtitle && !isBlank(config.subtitle(record)) && (
                <span className="lv-subtitle">{config.subtitle(record)}</span>
              )}
            </div>
          </div>
          <div className="lv-head-actions">
            {canEdit && (
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => navigate(`/sy-admin/${config.endpoint}/edit/${id}`)}
              >
                Edit
              </button>
            )}
            <Link to={listPath} className="btn btn-secondary">Back</Link>
          </div>
        </div>

        {/* ---------------------- gallery + quick facts ---------------------- */}
        <div className="lv-top">
          <Gallery images={record.images} featured={record.featuredImage} />

          <div className="lv-facts">
            {config.facts(record, { money: (v) => money(v, currency), formatDate })
              .filter((fact) => !isBlank(fact.value))
              .map((fact) => (
                <div className="lv-fact" key={fact.label}>
                  <span className="lv-fact-label">{fact.label}</span>
                  <strong className="lv-fact-value">{fact.value}</strong>
                </div>
              ))}
          </div>
        </div>

        {/* ------------------------- configured sections --------------------- */}
        {config.sections.map((section) => {
          if (section.kind === 'fields') {
            const fields = section.fields
              .map((field) => ({
                label: field.label,
                value: field.value ? field.value(record, { money: (v) => money(v, currency), formatDate }) : get(record, field.path),
              }))
              .filter((field) => !isBlank(field.value));
            if (fields.length === 0) return null;
            return (
              <Section title={section.title} key={section.title}>
                <div className="lv-fields">
                  {fields.map((field) => (
                    <Field key={field.label} label={field.label} value={field.value} />
                  ))}
                </div>
              </Section>
            );
          }

          if (section.kind === 'text') {
            const value = get(record, section.path);
            if (isBlank(value)) return null;
            return (
              <Section title={section.title} key={section.title}>
                <p className="lv-text">{value}</p>
              </Section>
            );
          }

          if (section.kind === 'chips') {
            const groups = section.groups(record).filter((group) => !isBlank(group.items));
            if (groups.length === 0) return null;
            return (
              <Section title={section.title} key={section.title}>
                <div className="lv-chip-groups">
                  {groups.map((group) => (
                    <div key={group.label}>
                      <span className="lv-group-label">{group.label}</span>
                      <Chips items={group.items} />
                    </div>
                  ))}
                </div>
              </Section>
            );
          }

          if (section.kind === 'cards') {
            const items = get(record, section.path) || [];
            if (items.length === 0) return null;
            return (
              <Section
                title={section.title}
                subtitle={`${items.length} ${items.length === 1 ? section.unit : `${section.unit}s`}`}
                key={section.title}
              >
                <div className="lv-items">
                  {items.map((item, index) => (
                    <ItemCard key={item._id || index} item={item} spec={section.item} currency={currency} />
                  ))}
                </div>
              </Section>
            );
          }

          return null;
        })}

        {/* ------------------------------- record ---------------------------- */}
        <Section title="Record">
          <div className="lv-fields">
            <Field label="Created" value={formatDate(record.createdAt)} />
            <Field label="Last updated" value={formatDate(record.updatedAt)} />
            <Field label="Verification expires" value={formatDate(record.verificationExpiresAt)} />
            <Field label="Featured expires" value={formatDate(record.featuredExpiresAt)} />
            <Field label="ID" value={<code className="lv-id">{record._id}</code>} />
          </div>
        </Section>
      </div>
    </Layout>
  );
};

export default ListingView;
